/**
 * ResultsGrid - Overhauled quiz results page.
 *
 * Why this matters: The new results experience needs to present the resolved
 * personality summary and the recommendation tiers separately, instead of
 * mixing old hardcoded profile copy with a flat 16-card catalog grid.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ResolvedQuizResult } from '../../utils/citrusQuizLogic';
import TreeCard from './TreeCard';
import QuizResultCTA from './QuizResultCTA';
import CitrusCelebration from './CitrusCelebration';

interface ResultsGridProps {
  userName: string;
  result: ResolvedQuizResult;
  onBack: () => void;
}

interface ActiveVideo {
  src: string;
  isVimeo: boolean;
}

// Why this matters: Catalog links are already Vimeo player URLs with muted=1,
// so the popup must normalize both player and plain Vimeo URLs before embedding.
function getVimeoEmbedUrl(videoLink: string): string | null {
  try {
    const url = new URL(videoLink);
    const hostname = url.hostname.replace(/^www\./, '');

    if (hostname === 'player.vimeo.com') {
      const params = new URLSearchParams(url.search);

      if (!params.has('autoplay')) params.set('autoplay', '1');
      params.set('muted', '0');
      if (!params.has('title')) params.set('title', '0');
      if (!params.has('byline')) params.set('byline', '0');
      if (!params.has('portrait')) params.set('portrait', '0');

      url.search = params.toString();
      return url.toString();
    }

    if (!hostname.endsWith('vimeo.com')) return null;

    const videoId = url.pathname
      .split('/')
      .find((segment) => /^\d+$/.test(segment));

    if (!videoId) return null;

    const params = new URLSearchParams(url.search);

    if (!params.has('autoplay')) params.set('autoplay', '1');
    params.set('muted', '0');
    if (!params.has('title')) params.set('title', '0');
    if (!params.has('byline')) params.set('byline', '0');
    if (!params.has('portrait')) params.set('portrait', '0');

    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
  } catch {
    return null;
  }
}

function getDirectVideoUrl(videoLink: string): string | null {
  try {
    const url = new URL(videoLink);

    return /\.(mp4|webm|ogg|mov)$/i.test(url.pathname) ? videoLink : null;
  } catch {
    return null;
  }
}

const ResultsGrid: React.FC<ResultsGridProps> = ({
  userName,
  result,
  onBack
}) => {
  const [shareMessage, setShareMessage] = useState('');
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  const displayName = userName?.trim() ? `${userName}, you're ${result.profile.name}.` : `You're ${result.profile.name}.`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://uscitrusnursery.com/quiz';
  const shareText = `I'm ${result.profile.name}! 🌳 Take the citrus quiz to find your perfect tree match → ${shareUrl}`;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'US Citrus Quiz',
          text: shareText,
          url: shareUrl,
        });
        setShareMessage('Shared.');
        return;
      }

      await navigator.clipboard.writeText(shareText);
      setShareMessage('Share text copied.');
    } catch (error) {
      setShareMessage('Share unavailable on this device.');
    }
  };

  const openVideoPopup = useCallback((videoLink: string): void => {
    const vimeoUrl = getVimeoEmbedUrl(videoLink);
    const directVideoUrl = getDirectVideoUrl(videoLink);

    if (!vimeoUrl && !directVideoUrl) return;

    if (document.activeElement instanceof HTMLElement) {
      previouslyFocusedElementRef.current = document.activeElement;
    }

    setActiveVideo({
      src: vimeoUrl ?? directVideoUrl ?? videoLink,
      isVimeo: Boolean(vimeoUrl),
    });
  }, []);

  const closeVideoPopup = useCallback((): void => {
    setActiveVideo(null);
  }, []);

  /**
   * Why this matters: The Escape listener only exists while the modal is open,
   * so repeated renders do not stack duplicate document-level event handlers.
   */
  useEffect(() => {
    if (!activeVideo) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveVideo(null);
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) {
        return;
      }

      const focusableElements = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], iframe, video, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('disabled'));

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    closeButtonRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElementRef.current?.focus();
    };
  }, [activeVideo]);

  return (
    <div className="results-grid">
      {/* UX-7: colorful confetti finale. Why this matters: this labels the actual animation and cleanup path. */}
      <CitrusCelebration />
      <div className="results-hero">
        <div className="results-hero-grid container">
          <div className="results-hero-column">
            <div className="results-content-container">
              <p className="results-kicker">Your Citrus Personality</p>
              <h1 className="results-title">{displayName}</h1>
              <h2 className="results-dynamic-headline">{result.headline}</h2>
              <p className="results-profile-description">{result.profile.description}</p>

              <div className="spirit-board">
                <div className="spirit-board-row spirit-board-title">
                  <div className="archetype-badge-compact">
                    <span className="archetype-icon">✨</span>
                    <span className="archetype-name">{result.archetype}</span>
                  </div>
                  <button className="quiz-btn quiz-btn-secondary results-share-btn" onClick={handleShare}>
                    Share My Result
                  </button>
                </div>
                <div className="spirit-board-row spirit-citrus-inline">
                  <small className="spirit-label">Your Spirit Citrus</small>
                  <span className="spirit-name">{result.spiritCitrus}</span>
                </div>
              </div>

              {shareMessage && <p className="results-share-message">{shareMessage}</p>}

              <div className="results-reading rte">
                {result.coreReading.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="para-font-family">{paragraph}</p>
                ))}
              </div>

              <div className="results-trees for-mobile">
                <div className="results-trees-header container">
                  <h2 className="results-section-title">Your Top Matches</h2>
                  <span className="results-trees-subtitle">
                    {result.spiritCitrus} fits your space and style especially well.
                  </span>
                </div>
                <div className="results-trees-grid">
                  {result.topMatches.map((treeScore, index) => (
                    <TreeCard
                      key={treeScore.tree.id}
                      treeScore={treeScore}
                      rank={index + 1}
                      onOpenVideo={openVideoPopup}
                    />
                  ))}
                </div>
              </div>

              <div className="quote-rows">
                <div className="quote-row rte">
                  <small className="quote-label">Your Superpower</small>
                  <span className="quote-summary"><b><i>{result.superpower}</i></b></span>
                </div>
                <div className="quote-row rte">
                  <small className="quote-label">Key Insight</small>
                  <span className="quote-summary"><b><i>{result.keyInsight}</i></b></span>
                </div>
                <div className="quote-row rte">
                  <small className="quote-label">Worth Remembering</small>
                  <span className="quote-summary"><b><i>{result.blindSpot}</i></b></span>
                </div>
              </div>

              <div className="results-trust-badges">
                <div className="trust-badge">
                  <span className="trust-icon">📚</span>
                  <span>Free 99-Page Care Guide</span>
                </div>
                <div className="trust-badge">
                  <span className="trust-icon">🌱</span>
                  <span>30-Day Replacement Guarantee</span>
                </div>
                <div className="trust-badge">
                  <span className="trust-icon">⭐</span>
                  <span>4.9/5 Rating • 10,000+ Happy Gardeners</span>
                </div>
              </div>
            </div>
          </div>
          <div className="results-hero-column">
            <div className="results-trees for-desktop">
              <div className="results-trees-header container">
                <h2 className="results-section-title">Your Top Matches</h2>
                <span className="results-trees-subtitle">
                  {result.spiritCitrus} fits your space and style especially well.
                </span>
              </div>
              <div className="results-trees-grid">
                {result.topMatches.map((treeScore, index) => (
                  <TreeCard
                    key={treeScore.tree.id}
                    treeScore={treeScore}
                    rank={index + 1}
                    onOpenVideo={openVideoPopup}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="results-trees-section results-trees-secondary">
        <div className="container results-secondary-layout">
          <div className="results-secondary-copy">
            <div className="faq-rows rte">
              <div className="faq-row">
                <h3>Will I receive care instructions with my tree?</h3>
                <p>Yes! Every order includes our printed Quick Start Guide, and you’ll also receive a digital full care guide by email. This walks you through watering, soil, pests, pruning, fertilizing, and more. No guesswork required.</p>
              </div>
              <div className="faq-row">
                <h3>What will my tree look like when it arrives?</h3>
                <div className="rte">
                  <p>Your tree will be:</p>
                  <p>~12&quot; tall (depending on variety)</p>
                  <p>Healthy, pruned, and grafted</p>
                  <p>May have young leaves, flowers, or baby fruit, depending on the season</p>
                  <p>Don’t worry if it looks a little sleepy—shipping stress is normal. It will perk up quickly with water and sunlight.</p>
                </div>
              </div>
              <div className="faq-row">
                <h3>Is this tree guaranteed?</h3>
                <p>Yes. We guarantee your tree arrives alive and healthy, or we’ll replace it. Just follow the included care steps and contact us within 30 days if there’s an issue. We’re here to help!</p>
              </div>
            </div>
          </div>

          {result.moreMatches.length > 0 && (
            <div className="results-secondary-trees">
              <div className="results-trees-header">
                <h2 className="results-section-title">You Might Also Love</h2>
                <span className="results-trees-subtitle">
                  A few more strong matches if you want to explore the field.
                </span>
              </div>
              <div className="results-trees-grid">
                {result.moreMatches.map((treeScore, index) => (
                  <TreeCard
                    key={treeScore.tree.id}
                    treeScore={treeScore}
                    rank={result.topMatches.length + index + 1}
                    onOpenVideo={openVideoPopup}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="results-footer-cta">
        <div className="container">
          <QuizResultCTA recommendations={result.recommendations} />

          <div className="results-footer-column results-email-reminder">
            <div className="email-reminder-content">
              <div className="email-reminder-icon">
                <img
                  className="reminder-icon"
                  src="https://cdn.shopify.com/s/files/1/0571/1167/5030/files/citrus-care-guide.png?v=1770146855"
                  alt="Citrus care guide"
                />
                <h3>Check your inbox!</h3>
              </div>
              <p>We’ve sent your personalised results and a free Citrus Care Guide to your email.</p><br />
              <p>Over the next few days, you’ll receive detailed information about your recommended trees.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="results-footer container">
        <button className="quiz-btn quiz-btn-back" onClick={onBack}>
          ← Retake Quiz
        </button>
        <a 
          href="https://uscitrusnursery.com/collections/citrus-trees" 
          target="_blank" 
          rel="noopener noreferrer"
          className="quiz-btn quiz-btn-secondary"
        >
          Browse All Trees
        </a>
      </div>

      <div
        ref={modalRef}
        className={`video-popup${activeVideo ? ' is-active' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Tree story video"
      >
        <button
          type="button"
          className="video-popup__overlay"
          aria-label="Close video overlay"
          onClick={closeVideoPopup}
        ></button>
        <div className="video-popup__box">
          <button
            ref={closeButtonRef}
            type="button"
            className="video-popup__close"
            aria-label="Close video"
            onClick={closeVideoPopup}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="video-popup__body">
            {activeVideo && (
              activeVideo.isVimeo ? (
                <iframe
                  title="Tree story video"
                  src={activeVideo.src}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  src={activeVideo.src}
                  autoPlay
                  controls
                  playsInline
                />
              )
            )}
          </div>
        </div>
      </div>

      <div className='result-cta-sticky container'>
        <QuizResultCTA recommendations={result.recommendations} />
      </div>
    </div>
  );
};

export default ResultsGrid;
